import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
  useReducer,
} from 'react';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import { Entity } from '../../types/entities/Entity';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import api from '../../services/api';
import Box from '@mui/material/Box';
import { debounce } from 'lodash';
import { SlidingPaneAddNew } from '../Page/SlidingPaneAddNew';
import { serviceProvider } from '../../schema/serviceProvider';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { corporatePayment } from '../../schema/corporatePayment';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { toaster } from '../../services/toaster';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import Fade from '@mui/material/Fade';
import CircularProgress from '@mui/material/CircularProgress';
import { accommodationCorporate } from '../../schema/accommodationCorporate';
import { accommodation } from '../../schema/accommodation';

type Props<T extends Entity> = {
  values: any;
  onChange: (value: any, column: string) => void;
};

const reducer = (state: any, action: any) => {
  switch (action.type) {
    case 'SET_INPUT':
      const newCurrent: any = [...state.current];

      if (action.payload.type === 'corporate') {
        const updatedRow = newCurrent.map((row: any) => {
          return row?.id === action.payload.id
            ? { ...row, [action.payload.column]: action.payload.value }
            : row;
        });

        return {
          ...state,
          current: updatedRow,
          send: true,
        };
      }

      const updatedRow = newCurrent.map((row: any) => {
        row.corporate_payments = row.corporate_payments.map((item: any) => {
          return item?.id === action.payload.id
            ? { ...item, [action.payload.column]: action.payload.value }
            : item;
        });
        return row;
      });

      return {
        ...state,
        current: updatedRow,
        send: true,
      };
    case 'SET_HISTORY':
      const tempAction: any = { ...action };

      return {
        ...state,
        history: [tempAction.payload],
        current: tempAction.payload,
        send: false,
      };
    default:
      return state;
  }
};

export const AccommodationCorporateComponent = <T extends Entity>({
  values,
  onChange,
}: Props<T>): ReactElement => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [properties, setProperties] = useState<any>([]);
  const [schema, setSchema] = useState<SchemaWithClient<any>>(
    accommodationCorporate
  );
  const [paymentSchema, setPaymentSchema] = useState<SchemaWithClient<any>>(
    corporatePayment
  );

  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [currentAddNewPaneEntity, setCurrentAddNewPaneEntity] = React.useState<
    any
  >({
    schema: schema,
    row: {},
  });

  const [query, setQuery] = React.useState<'idle' | 'progress' | 'success'>(
    'idle'
  );
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();

  const [
    currentAddNewPaymentPaneEntity,
    setCurrentAddNewPaymentPaneEntity,
  ] = React.useState<any>({
    schema: paymentSchema,
    row: {},
  });

  const [state, dispatch] = useReducer(reducer, {
    history: [],
    current: [],
    pointer: 0,
    send: false,
  });

  const fetchData = useCallback(() => {
    const searchParams = new URLSearchParams({});

    api
      .get(`/accommodation-corporates/${values.id}`, searchParams)
      .then((res: any) => {
        dispatch({
          type: 'SET_HISTORY',
          payload: res,
        });
      });
  }, []);

  const handleChange = useCallback(
    async (column: any, value: any, schema: any, id: number) => {
      setQuery('progress');

      schema.client
        .update(id, {
          [column]: value,
        })
        .then((res: any) => {
          timerRef.current = setTimeout(() => {
            setQuery('success');
          }, 2000);
        })
        .catch((e: any) => {
          console.error(e);
        });
    },
    [properties]
  );

  const handleSaveData = (
    column: any,
    value: any,
    type: any,
    mainData: any
  ) => {
    dispatch({
      type: 'SET_INPUT',
      payload: {
        type: type,
        id: mainData?.id,
        column: column,
        value: value,
      },
    });
  };

  const debouncedChange = useCallback(
    debounce(() => {
      if (state.send) {
        onChange(state.current, 'corporates');
      }
    }, 500),
    [state]
  );

  useEffect(() => {
    debouncedChange();
  }, [state, debouncedChange]);

  useEffect(() => {
    fetchData();
  }, []);

  React.useEffect(
    () => () => {
      clearTimeout(timerRef.current);
    },
    []
  );

  return (
    <>
      <Stack width={'100%'}>
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Typography variant="h6" component="div">
            Corporate Data
          </Typography>
          <IconButton
            onClick={() => {
              setCurrentAddNewPaneEntity({
                row: {
                  id: 'new',
                  accommodation: {
                    id: values?.id,
                    name: values?.name,
                  },
                },
                schema: accommodationCorporate,
              });
            }}
          >
            <AddIcon />
          </IconButton>
          <Box sx={{}}>
            {query === 'success' ? (
              <Stack direction={'row'} alignItems={'center'}>
                <CloudDoneRoundedIcon color={'disabled'} />
                <Typography
                  variant={'caption'}
                  marginLeft={'5px'}
                  color={'gray'}
                >
                  successfully saved
                </Typography>
              </Stack>
            ) : (
              <Fade
                in={query === 'progress'}
                style={{
                  transitionDelay: query === 'progress' ? '800ms' : '0ms',
                }}
                unmountOnExit
              >
                <CircularProgress size={20} />
              </Fade>
            )}
          </Box>
        </Stack>
      </Stack>

      {loading ? (
        <>
          <Skeleton
            sx={{
              mt: '20px',
            }}
            variant="rounded"
            width={'100%'}
            height={350}
          />
        </>
      ) : (
        <>
          <Grid container spacing={2}>
            {state.current.map((corporateItem: any) => {
              return (
                <Grid item xs={6}>
                  <Box
                    component="section"
                    sx={{
                      p: '20px',
                      background: '#e1e1e1',
                      borderRadius: '10px',
                      mt: '20px',
                      width: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={0}
                      sx={{
                        width: '100%',
                      }}
                      alignItems={'center'}
                    >
                      <Stack
                        sx={{
                          width: '100%',
                        }}
                      >
                        {(Object.keys(schema.columns) as Array<keyof T>).map(
                          (column, index: number) => {
                            const {
                              readOnly,
                              name,
                              fieldRenderer: Field,
                              slidePaneFieldRenderer: FieldForPane,
                              accordingTo,
                              hide,
                              nameForSidePane,
                              required,
                            } = schema.columns[column as keyof {}];

                            return (
                              <>
                                <Stack
                                  key={index}
                                  direction="row"
                                  spacing={0}
                                  sx={{
                                    width: '100%',
                                    display: hide ? 'none' : '',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      flex: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      background: '#e1e1e1',
                                      '& .MuiFormControl-root.MuiTextField-root': {
                                        background: '#fff',
                                      },
                                    }}
                                  >
                                    <span className={'px-[8px]'}>{`${
                                      nameForSidePane ? nameForSidePane : name
                                    }`}</span>
                                  </Box>

                                  <Box
                                    sx={{
                                      flex: 2,
                                      borderBottom: '1px solid #dfdbdb',
                                      borderRight: '1px solid #dfdbdb',
                                      '& .MuiFormControl-root.MuiTextField-root': {
                                        width: '100%',
                                        background: '#fff',
                                      },
                                      maxWidth: 'calc(100% - 204px)',
                                    }}
                                  >
                                    {FieldForPane ? (
                                      <FieldForPane
                                        onChange={(value: T[typeof column]) => {
                                          console.log(column, value);
                                          handleSaveData(
                                            column,
                                            value,
                                            'corporate',
                                            corporateItem
                                          );
                                        }}
                                        defaultValue={corporateItem[column]}
                                        isActive={true}
                                        disabled={readOnly}
                                        intent={undefined}
                                        column={schema.columns[column]}
                                        isAddingNew={true}
                                        allValue={corporateItem}
                                        mainSchema={schema}
                                      />
                                    ) : (
                                      <></>
                                    )}
                                  </Box>
                                </Stack>
                              </>
                            );
                          }
                        )}
                      </Stack>

                      <IconButton
                        onClick={() => {
                          setOpenConfirmDialog({
                            row: corporateItem,
                            tab: 'corporates',
                            schema: schema,
                          });
                        }}
                      >
                        <DeleteRoundedIcon color={'error'} />
                      </IconButton>
                    </Stack>

                    <Accordion
                      sx={{
                        marginTop: '10px',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                          },
                        }}
                      >
                        <Typography variant="h6" component="div">
                          Payments
                        </Typography>
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            setCurrentAddNewPaymentPaneEntity({
                              row: {
                                id: 'new',
                                corporate: {
                                  id: corporateItem?.id,
                                  name: corporateItem?.name,
                                },
                              },
                              schema: corporatePayment,
                            });
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {corporateItem.corporate_payments.map(
                            (payment: any) => {
                              return (
                                <Grid item xs={12}>
                                  <Box
                                    component="section"
                                    sx={{
                                      p: '20px',
                                      background: '#e1e1e1',
                                      borderRadius: '10px',
                                      width: '100%',
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={0}
                                      sx={{
                                        width: '100%',
                                      }}
                                      alignItems={'center'}
                                    >
                                      <Stack
                                        sx={{
                                          width: '100%',
                                        }}
                                      >
                                        {(Object.keys(
                                          paymentSchema.columns
                                        ) as Array<keyof T>).map(
                                          (column, index: number) => {
                                            const {
                                              readOnly,
                                              name,
                                              fieldRenderer: Field,
                                              slidePaneFieldRenderer: FieldForPane,
                                              accordingTo,
                                              hide,
                                              nameForSidePane,
                                              required,
                                            } = paymentSchema.columns[
                                              column as keyof {}
                                            ];

                                            if (
                                              column === 'corporate' ||
                                              column === 'name'
                                            ) {
                                              return <></>;
                                            }

                                            return (
                                              <>
                                                <Stack
                                                  key={index}
                                                  direction="row"
                                                  spacing={0}
                                                  sx={{
                                                    width: '100%',
                                                    display: hide ? 'none' : '',
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      flex: 1,
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      background: '#e1e1e1',
                                                      '& .MuiFormControl-root.MuiTextField-root': {
                                                        background: '#fff',
                                                      },
                                                    }}
                                                  >
                                                    <span
                                                      className={'px-[8px]'}
                                                    >{`${
                                                      nameForSidePane
                                                        ? nameForSidePane
                                                        : name
                                                    }`}</span>
                                                  </Box>

                                                  <Box
                                                    sx={{
                                                      flex: 2,
                                                      borderBottom:
                                                        '1px solid #dfdbdb',
                                                      borderRight:
                                                        '1px solid #dfdbdb',
                                                      '& .MuiFormControl-root.MuiTextField-root': {
                                                        width: '100%',
                                                        background: '#fff',
                                                      },
                                                      maxWidth:
                                                        'calc(100% - 204px)',
                                                    }}
                                                  >
                                                    {FieldForPane ? (
                                                      <FieldForPane
                                                        onChange={(
                                                          value: T[typeof column]
                                                        ) => {
                                                          console.log(
                                                            column,
                                                            value
                                                          );
                                                          handleSaveData(
                                                            column,
                                                            value,
                                                            'corporate_payment',
                                                            payment
                                                          );
                                                        }}
                                                        defaultValue={
                                                          payment[column]
                                                        }
                                                        isActive={true}
                                                        disabled={readOnly}
                                                        intent={undefined}
                                                        column={
                                                          paymentSchema.columns[
                                                            column
                                                          ]
                                                        }
                                                        isAddingNew={true}
                                                        allValue={payment}
                                                        mainSchema={
                                                          serviceProvider
                                                        }
                                                      />
                                                    ) : (
                                                      <></>
                                                    )}
                                                  </Box>
                                                </Stack>
                                              </>
                                            );
                                          }
                                        )}
                                      </Stack>

                                      <IconButton
                                        onClick={() => {
                                          setOpenConfirmDialog({
                                            row: payment,
                                            tab: 'corporate_payments',
                                            schema: corporatePayment,
                                          });
                                        }}
                                      >
                                        <DeleteRoundedIcon color={'error'} />
                                      </IconButton>
                                    </Stack>
                                  </Box>
                                </Grid>
                              );
                            }
                          )}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {currentAddNewPaneEntity?.row?.id && (
        <>
          <SlidingPaneAddNew
            schema={currentAddNewPaneEntity?.schema}
            mainSchema={accommodation}
            id={currentAddNewPaneEntity?.row?.id}
            hasSlidingPane={false}
            onSubmit={(result: any) => {
              const newData = [...properties];
              newData.push(result);
              setProperties(newData);

              setCurrentAddNewPaneEntity({
                schema: schema,
                row: {},
                key: undefined,
              });
            }}
            propertiesFromTable={
              currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
            }
            row={
              currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
            }
            onClose={(value) => {
              setCurrentAddNewPaneEntity({
                schema: schema,
                row: {},
                key: undefined,
              });
            }}
          />
        </>
      )}
      {currentAddNewPaymentPaneEntity?.row?.id && (
        <>
          <SlidingPaneAddNew
            schema={currentAddNewPaymentPaneEntity?.schema}
            mainSchema={serviceProvider}
            id={currentAddNewPaymentPaneEntity?.row?.id}
            hasSlidingPane={false}
            onSubmit={(value: any) => {
              const newData = [...state.current];
              const newPayments = newData.map((corporate: any) => {
                if (
                  corporate?.id ===
                  currentAddNewPaymentPaneEntity?.row.corporate.id
                ) {
                  corporate.corporate_payments.push(value);
                }
                return corporate;
              });

              dispatch({
                type: 'SET_HISTORY',
                payload: newPayments,
              });
              setCurrentAddNewPaymentPaneEntity({
                schema: schema,
                row: {},
                key: undefined,
              });
            }}
            propertiesFromTable={
              currentAddNewPaymentPaneEntity?.row
                ? currentAddNewPaymentPaneEntity?.row
                : {}
            }
            row={
              currentAddNewPaymentPaneEntity?.row
                ? currentAddNewPaymentPaneEntity?.row
                : {}
            }
            onClose={(value) => {
              setCurrentAddNewPaymentPaneEntity({
                schema: schema,
                row: {},
                key: undefined,
              });
            }}
          />
        </>
      )}

      {openConfirmDialog && (
        <Dialog
          open={openConfirmDialog}
          onClose={() => {
            setOpenConfirmDialog(false);
          }}
        >
          <DialogTitle>{'Are you sure you want to delete it?'}</DialogTitle>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenConfirmDialog(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant={'contained'}
              color={'error'}
              onClick={() => {
                const deletedRow = openConfirmDialog?.row;

                openConfirmDialog?.schema.client
                  .delete(deletedRow.id as number, { id: deletedRow.id })
                  .then((res: any) => {
                    if (openConfirmDialog.tab === 'corporates') {
                      const tempProperties = [...state.current];
                      const newRows = tempProperties.filter(
                        (corporate: any) => {
                          return corporate?.id !== deletedRow?.id;
                        }
                      );
                      dispatch({
                        type: 'SET_HISTORY',
                        payload: newRows,
                      });
                    } else {
                      const tempProperties = [...state.current];
                      const newRows = tempProperties.map((corporate: any) => {
                        corporate.corporate_payments = corporate.corporate_payments.filter(
                          (payment: any) => {
                            return payment?.id !== deletedRow?.id;
                          }
                        );
                        return corporate;
                      });
                      dispatch({
                        type: 'SET_HISTORY',
                        payload: newRows,
                      });
                    }

                    toaster.show({
                      intent: 'success',
                      message: `Successfully deleted ${
                        openConfirmDialog?.schema
                      }: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`,
                    });
                    setOpenConfirmDialog(false);
                  })
                  .catch((e: any) => {
                    console.log(e);
                    toaster.show({
                      icon: 'error',
                      message: `Error: Unable to delete ${
                        openConfirmDialog?.schema
                      }: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`,
                      intent: 'danger',
                    });
                  });
              }}
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/*{openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {}}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={(value) => {
            const serviceProviders = [...properties[`service_provider`]];
            const newData = serviceProviders.map((provider: any) => {
              provider.contacts = provider.contacts.map((contact: any) => {
                return contact?.id === value?.id ? value : contact;
              });

              return provider;
            });
            setOpenedSlidingPane({});

            setProperties({
              ...properties,
              [`service_provider`]: newData,
            });

            return true;
          }}
        />
      )}*/}
    </>
  );
};
